import React, { useEffect, useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { ChevronDown, ChevronUp, ClipboardList, Pencil, Plus, Trash2, X, Check } from 'lucide-react';
import toast from 'react-hot-toast';
import { updateStageQualificationProfile } from '../../services/api/implementation/impl';

const MULTISELECT_CHIP_FIELDS = new Set(['positive_intent', 'strong_buying_signal']);

const CHIP_SECTION_LABELS = {
  positive_intent: 'Intent signals',
  strong_buying_signal: 'Buying signals'
};

/** Core fields shown first — matches qualification mock density. */
const PRIMARY_FIELD_ORDER = [
  'meaningful_response',
  'requirement',
  'purchase_purpose',
  'budget_discussed',
  'approximate_budget',
  'buying_timeline',
  'current_blocker',
  'next_follow_up_datetime',
  'next_action',
  'lead_response_quality',
  'requirement_confirmed',
  'unit_considering',
  'budget_confirmed',
  'confirmed_budget_range',
  'hot_buying_timeline',
  'price_discussed',
  'payment_discussed',
  'decision_maker_involved',
  'site_visit_status',
  'closing_next_action'
];

const PRIMARY_VISIBLE_COUNT = 10;

function formatMovedAt(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function fieldSortIndex(fieldId) {
  const idx = PRIMARY_FIELD_ORDER.indexOf(fieldId);
  return idx === -1 ? PRIMARY_FIELD_ORDER.length + 1 : idx;
}

const MULTISELECT_CHIP_LABELS = {
  asked_price: 'Price',
  asked_units: 'Units',
  asked_payment_plan: 'Payment plan',
  asked_site_visit: 'Site visit',
  asked_location: 'Location',
  asked_brochure: 'Brochure',
  asked_rera: 'RERA/docs',
  asked_loan: 'Loan',
  asked_discount: 'Discount',
  family_discussion: 'Family talk',
  called_back: 'Called back',
  site_visit_done: 'Visit done',
  family_site_visit: 'Family visit',
  final_price: 'Final price',
  booking_amount: 'Booking amt',
  hold_unit: 'Hold unit',
  agreement_process: 'Agreement',
  loan_process: 'Loan process',
  payment_schedule: 'Pay schedule',
  negotiation_active: 'Negotiation',
  senior_discussion: 'Senior call',
  token_expected: 'Token soon',
  other: 'Other'
};

function chipLabelFromToken(token) {
  const key = String(token ?? '').trim();
  if (!key) return '';
  if (MULTISELECT_CHIP_LABELS[key]) return MULTISELECT_CHIP_LABELS[key];
  return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function chipsFromMultiselectCard(card) {
  const raw = card.rawValue || card.value;
  if (!raw || raw === '—') return [];

  if (Array.isArray(card.rawValue)) {
    return card.rawValue.map(chipLabelFromToken).filter(Boolean);
  }

  const rawText = String(raw);
  if (rawText.includes(',') && !rawText.includes(' +')) {
    return rawText
      .split(',')
      .map((part) => chipLabelFromToken(part))
      .filter(Boolean);
  }

  const { chips, overflow } = parseChipLabels(rawText);
  if (overflow > 0) return chips;
  return chips;
}

function parseChipLabels(value) {
  if (!value || value === '—') return { chips: [], overflow: 0 };
  const parts = String(value)
    .split(/,\s*/)
    .map((part) => part.trim())
    .filter(Boolean);
  if (!parts.length) return { chips: [], overflow: 0 };

  const last = parts[parts.length - 1];
  const overflowMatch = last.match(/^(.+?)\s+\+(\d+)$/);
  if (overflowMatch) {
    return {
      chips: [...parts.slice(0, -1), overflowMatch[1]],
      overflow: Number.parseInt(overflowMatch[2], 10) || 0
    };
  }
  return { chips: parts, overflow: 0 };
}

function splitSectionCards(cards = []) {
  const gridCards = [];
  const chipGroups = [];

  for (const card of cards) {
    if (MULTISELECT_CHIP_FIELDS.has(card.fieldId)) {
      const chips = chipsFromMultiselectCard(card);
      if (chips.length) {
        chipGroups.push({
          fieldId: card.fieldId,
          label: CHIP_SECTION_LABELS[card.fieldId] ?? card.label,
          chips,
          overflow: 0
        });
      }
      continue;
    }
    gridCards.push(card);
  }

  gridCards.sort(
    (a, b) => fieldSortIndex(a.fieldId) - fieldSortIndex(b.fieldId) || a.label.localeCompare(b.label)
  );

  return {
    primaryGrid: gridCards.slice(0, PRIMARY_VISIBLE_COUNT),
    secondaryGrid: gridCards.slice(PRIMARY_VISIBLE_COUNT),
    chipGroups
  };
}

function ProfileField({ label, value }) {
  return (
    <div className="min-w-0">
      <p className="text-[9px] font-bold uppercase tracking-[0.08em] text-gray-400 leading-none">
        {label}
      </p>
      <p
        className="mt-1 text-sm font-bold text-gray-900 leading-snug break-words"
        title={value}>
        {value}
      </p>
    </div>
  );
}

function ProfileGrid({ cards }) {
  if (!cards.length) return null;
  return (
    <div className="grid grid-cols-2 gap-x-4 gap-y-2.5">
      {cards.map((card) => (
        <ProfileField key={card.fieldId} label={card.label} value={card.value} />
      ))}
    </div>
  );
}

function IntentChips({ label, chips, overflow = 0 }) {
  return (
    <div className="border-t border-teal-100/80 pt-2">
      <p className="text-[9px] font-bold uppercase tracking-[0.08em] text-gray-400 leading-none">
        {label}
      </p>
      <div className="mt-1.5 flex flex-wrap gap-1">
        {chips.map((chip) => (
          <span
            key={chip}
            className="inline-flex max-w-full rounded-full bg-teal-50 px-2.5 py-0.5 text-[11px] font-medium text-[#1E4A53] ring-1 ring-teal-100"
            title={chip}>
            {chip}
          </span>
        ))}
        {overflow > 0 ? (
          <span
            className="inline-flex rounded-full bg-teal-50 px-2.5 py-0.5 text-[11px] font-semibold text-[#1E4A53] ring-1 ring-teal-100"
            title={`${overflow} more signal${overflow === 1 ? '' : 's'}`}>
            +{overflow}
          </span>
        ) : null}
      </div>
    </div>
  );
}

function SectionContent({ cards, defaultExpanded = true }) {
  const { primaryGrid, secondaryGrid, chipGroups } = splitSectionCards(cards);
  const [showMore, setShowMore] = useState(
    () => defaultExpanded && secondaryGrid.length > 0
  );

  useEffect(() => {
    setShowMore(defaultExpanded && secondaryGrid.length > 0);
  }, [cards, defaultExpanded, secondaryGrid.length]);

  const visibleGrid = showMore
    ? [...primaryGrid, ...secondaryGrid]
    : primaryGrid;

  return (
    <div className="space-y-2">
      <ProfileGrid cards={visibleGrid} />

      {secondaryGrid.length > 0 ? (
        <button
          type="button"
          onClick={() => setShowMore((v) => !v)}
          className="inline-flex items-center gap-0.5 text-[11px] font-semibold text-[#1E4A53] hover:underline">
          {showMore ? (
            <>
              Show less <ChevronUp className="h-3 w-3" />
            </>
          ) : (
            <>
              {secondaryGrid.length} more detail{secondaryGrid.length === 1 ? '' : 's'}{' '}
              <ChevronDown className="h-3 w-3" />
            </>
          )}
        </button>
      ) : null}

      {chipGroups.map((group) => (
        <IntentChips
          key={group.fieldId}
          label={group.label}
          chips={group.chips}
          overflow={group.overflow}
        />
      ))}
    </div>
  );
}

function StageTransitionBadge({ label }) {
  if (!label) return null;
  const parts = label.split('→').map((part) => part.trim()).filter(Boolean);
  if (parts.length === 2) {
    return (
      <div className="flex items-center gap-1 shrink-0">
        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-600">
          {parts[0]}
        </span>
        <span className="text-[10px] text-gray-300">→</span>
        <span className="rounded-full bg-teal-50 px-2 py-0.5 text-[10px] font-semibold text-[#1E4A53] ring-1 ring-teal-100">
          {parts[1]}
        </span>
      </div>
    );
  }
  return (
    <span className="rounded-full bg-teal-50 px-2.5 py-0.5 text-[10px] font-semibold text-[#1E4A53] ring-1 ring-teal-100 whitespace-nowrap">
      {label}
    </span>
  );
}

function SectionView({ section, onEdit, showBadge = true, defaultExpanded = true }) {
  const badgeLabel =
    section.title ||
    (section.fromPstatus && section.toPstatus
      ? `${section.fromPstatus} → ${section.toPstatus}`
      : null);

  return (
    <div className="space-y-2.5">
      {showBadge && badgeLabel ? (
        <div className="flex items-center justify-between gap-2">
          <p className="text-[10px] text-gray-400">Updated {formatMovedAt(section.movedAt)}</p>
          <div className="flex shrink-0 items-center gap-1">
            <span className="rounded-full bg-teal-50 px-2.5 py-0.5 text-[10px] font-semibold text-[#1E4A53] ring-1 ring-teal-100 whitespace-nowrap">
              {badgeLabel}
            </span>
            <button
              type="button"
              onClick={onEdit}
              className="rounded p-1 text-gray-400 hover:bg-teal-50 hover:text-[#1E4A53]"
              title="Edit answers">
              <Pencil className="h-3 w-3" />
            </button>
          </div>
        </div>
      ) : null}

      <SectionContent cards={section.cards} defaultExpanded={defaultExpanded} />
    </div>
  );
}

function EditFieldRow({ field, onChange, onRemove }) {
  return (
    <div className="rounded-lg border border-teal-100 bg-white p-2 space-y-1.5">
      {field.isCustom ? (
        <input
          type="text"
          value={field.label}
          onChange={(e) => onChange({ ...field, label: e.target.value })}
          placeholder="Question"
          className="w-full rounded border border-gray-200 px-2 py-1 text-[11px] font-medium text-gray-600 focus:border-teal-400 focus:outline-none"
        />
      ) : (
        <p className="text-[9px] font-semibold uppercase tracking-[0.08em] text-gray-400">
          {field.label}
        </p>
      )}
      <div className="flex items-start gap-1">
        <textarea
          rows={field.isCustom ? 2 : 1}
          value={field.value}
          onChange={(e) => onChange({ ...field, value: e.target.value })}
          placeholder="Answer"
          className="min-h-[2rem] flex-1 rounded border border-gray-200 px-2 py-1 text-sm font-medium text-gray-900 focus:border-teal-400 focus:outline-none resize-y"
        />
        {field.isCustom && !field.isNew ? (
          <button
            type="button"
            onClick={() => onRemove(field.fieldId)}
            className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600"
            title="Remove question">
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        ) : null}
      </div>
    </div>
  );
}

function SectionEditor({ section, leadId, onSaved, onCancel }) {
  const [fields, setFields] = useState(() =>
    (section.cards || []).map((card) => ({
      fieldId: card.fieldId,
      label: card.label,
      value: card.rawValue ?? card.value ?? '',
      isCustom: !!card.isCustom,
      isNew: false
    }))
  );
  const [removedIds, setRemovedIds] = useState([]);

  const saveMutation = useMutation({
    mutationFn: () => {
      const existing = fields.filter((f) => !f.isNew);
      const added = fields.filter((f) => f.isNew);
      return updateStageQualificationProfile({
        leadId,
        qualificationId: section.qualificationId,
        updates: existing.map((f) => ({
          fieldId: f.fieldId,
          value: f.value,
          ...(f.isCustom ? { label: f.label } : {})
        })),
        add: added.map((f) => ({ label: f.label, value: f.value })),
        removeFieldIds: removedIds
      });
    },
    onSuccess: (data) => {
      toast.success('Qualification profile saved');
      onSaved(data);
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || 'Could not save profile');
    }
  });

  const addField = () => {
    setFields((prev) => [
      ...prev,
      {
        fieldId: `new_${Date.now()}`,
        label: '',
        value: '',
        isCustom: true,
        isNew: true
      }
    ]);
  };

  const handleRemove = (fieldId) => {
    setRemovedIds((prev) => [...prev, fieldId]);
    setFields((prev) => prev.filter((f) => f.fieldId !== fieldId));
  };

  return (
    <div className="space-y-2 rounded-lg border border-teal-200 bg-teal-50/40 p-2.5">
      <div className="flex items-center justify-between gap-2">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-[#1E4A53]">
          Editing {section.title}
        </p>
        <div className="flex gap-1">
          <button
            type="button"
            onClick={onCancel}
            className="rounded p-1 text-gray-500 hover:bg-white"
            title="Cancel">
            <X className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isPending}
            className="rounded p-1 text-[#1E4A53] hover:bg-white disabled:opacity-50"
            title="Save">
            <Check className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
      <div className="space-y-1.5 max-h-52 overflow-y-auto">
        {fields.map((field) => (
          <EditFieldRow
            key={field.fieldId}
            field={field}
            onChange={(next) =>
              setFields((prev) => prev.map((f) => (f.fieldId === next.fieldId ? next : f)))
            }
            onRemove={handleRemove}
          />
        ))}
      </div>
      <button
        type="button"
        onClick={addField}
        className="inline-flex w-full items-center justify-center gap-1 rounded-lg border border-dashed border-teal-200 bg-white py-1.5 text-[11px] font-semibold text-[#1E4A53] hover:bg-teal-50">
        <Plus className="h-3 w-3" />
        Add question
      </button>
    </div>
  );
}

export default function StageQualificationProfilePanel({
  leadId,
  cards = [],
  sections = [],
  onUpdated,
  embedded = false
}) {
  const [editingSectionId, setEditingSectionId] = useState(null);

  useEffect(() => {
    setEditingSectionId(null);
  }, [sections, cards]);

  const hasSections = sections.length > 0;
  const flatCards = cards.length ? cards : sections.flatMap((s) => s.cards || []);

  const latestUpdated = useMemo(() => {
    if (!sections.length) return null;
    const dates = sections.map((s) => s.movedAt).filter(Boolean);
    if (!dates.length) return null;
    return dates.sort((a, b) => new Date(b).getTime() - new Date(a).getTime())[0];
  }, [sections]);

  const primarySection = sections[0];
  const primaryBadge =
    primarySection?.title ||
    (primarySection?.fromPstatus && primarySection?.toPstatus
      ? `${primarySection.fromPstatus} → ${primarySection.toPstatus}`
      : null);

  if (!flatCards.length) return null;

  const handleSaved = (data) => {
    setEditingSectionId(null);
    onUpdated?.(data);
  };

  const shellClass = embedded
    ? 'space-y-0'
    : 'rounded-xl border border-teal-100 bg-white shadow-sm overflow-hidden';

  return (
    <div className={shellClass}>
      <div className={`${embedded ? 'px-3 pt-3 pb-2' : 'border-b border-teal-50 px-4 py-3'}`}>
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5">
              <ClipboardList className="h-3.5 w-3.5 shrink-0 text-[#1E4A53]" />
              <p className="text-sm font-bold text-gray-900 leading-tight">Qualification profile</p>
            </div>
            {latestUpdated && !editingSectionId ? (
              <p className="mt-0.5 text-[10px] text-gray-400">
                Updated {formatMovedAt(latestUpdated)}
              </p>
            ) : null}
          </div>
          {hasSections && sections.length === 1 && primaryBadge && !editingSectionId ? (
            <div className="flex shrink-0 items-center gap-1">
              <StageTransitionBadge label={primaryBadge} />
              <button
                type="button"
                onClick={() => setEditingSectionId(sections[0].qualificationId)}
                className="rounded p-1 text-gray-400 hover:bg-teal-50 hover:text-[#1E4A53]"
                title="Edit answers">
                <Pencil className="h-3.5 w-3.5" />
              </button>
            </div>
          ) : null}
        </div>
      </div>

      <div className={`${embedded ? 'px-3 pb-2' : 'px-4 py-4'} space-y-2`}>
        {hasSections
          ? sections.map((section, index) =>
              editingSectionId === section.qualificationId ? (
                <SectionEditor
                  key={section.qualificationId}
                  section={section}
                  leadId={leadId}
                  onSaved={handleSaved}
                  onCancel={() => setEditingSectionId(null)}
                />
              ) : (
                <div
                  key={section.qualificationId}
                  className={index > 0 ? 'border-t border-teal-50 pt-4' : ''}>
                  <SectionView
                    section={section}
                    onEdit={() => setEditingSectionId(section.qualificationId)}
                    showBadge={sections.length > 1}
                    defaultExpanded={embedded}
                  />
                </div>
              )
            )
          : (
            <SectionContent cards={flatCards} defaultExpanded={embedded} />
          )}
      </div>
    </div>
  );
}
